import { useCurrentUser } from '@/app/providers/user';
import { NotificationCenter } from '@/app/layout/NotificationCenter';
import { Sidebar } from '@/app/layout/Sidebar';
import LandingPage from '@/features/landing/pages/LandingPage';
import PromptPage from '@/features/generation/pages/PromptPage';

/**
 * `/` is two pages, and this picks one.
 *
 * Signed in it is `PromptPage`, unchanged — same path, same shell, same sidebar
 * link, so nothing about the authenticated app moves. Signed out it is the public
 * landing page: real videos, playable, with a Get started CTA.
 *
 * Why the branch lives here rather than in the route table: `/` cannot be both
 * guarded and public, and the two branches need different shells. The signed-in
 * root belongs inside `AppLayout` (left rail, content card) which it gets from its
 * parent route; the landing page draws its own top bar and owns the viewport. This
 * component sits inside the guarded route, so `PromptPage` keeps its layout for
 * free, and the public branch replaces the whole subtree when there is no user.
 *
 * It therefore has to render BEFORE `RequireUser` would redirect — see App.tsx,
 * where `/` is deliberately lifted out of the guarded group for exactly that
 * reason, and re-wrapped in `AppLayout` on the authenticated side.
 *
 * `status === 'loading'` renders NOTHING rather than either branch.
 *
 * It used to render the public one, reasoning that an anonymous first visit is the
 * common case and a spinner would flash the front door blank. That was true as far
 * as it went, but it costed more than a frame of the wrong picture: mounting
 * LandingPage fires `GET /home/showcase` and starts an autoplaying `<video>`, so
 * every authenticated hard refresh paid for a request and a media connection it
 * then threw away.
 *
 * Nothing, not a spinner: the boot refresh resolves in milliseconds, so a spinner
 * would itself be a flash. An empty frame costs an anonymous visitor a few
 * milliseconds of blank, and costs a signed-in one nothing at all.
 */
export default function RootRoute() {
  const { status } = useCurrentUser();

  if (status === 'authenticated') {
    /* The same markup AppLayout produces (shell > Sidebar + shell__content), not a
       reuse of it: AppLayout renders an `<Outlet />`, so it can only wrap a child
       ROUTE, and `/` has to resolve its element before the router would descend.
       The classes are the contract here -- shell.css styles them, and both shells
       stay in step because neither states a dimension. */
    return (
      <div className="shell">
        <Sidebar />
        <main className="shell__content">
          <PromptPage />
        </main>
        <NotificationCenter />
      </div>
    );
  }

  /* No wrapping shell: LandingPage brings its own `.shell` row, because it owns a
     rail and the rail needs to be a flex sibling of the content. Nesting the two
     put a `display: block` bare shell around a `display: flex` one, which
     collapsed the row.

     NotificationCenter still belongs here rather than inside the page -- both real
     layouts mount one, and without it a toast raised on this route (a failed load)
     has nowhere to render. */
  /* Nothing while the session refresh is in flight -- see the note above on why
     this is not the public branch and not a spinner. NotificationCenter stays
     mounted so a toast raised during boot still has somewhere to render. */
  if (status === 'loading') {
    return <NotificationCenter />;
  }

  return (
    <>
      <LandingPage />
      <NotificationCenter />
    </>
  );
}
